import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import { Box, Menu, MenuItem, Typography } from '@mui/material';

import selectUser from '../../../../../../../store/selectors/appSelector';
import { BLACK, PRIMARY_PURPLE } from '../../../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../../../theme/fontConstants';
import { documentStateStatuses } from '../constants';
import { canWriteDocumentReviewState } from '../documentReview';

import DocumentReviewSubmenu from './DocumentReviewSubmenu';
import FormStatusMenuItems from './FormStatusMenuItems';
import FormValidityMenuItems from './FormValidityMenuItems';
import DocumentCardMenuIcon from './icons';
import useDocumentMenu, { DOCUMENT_SUBMENU_KEYS } from './useDocumentMenu';

export const DOCUMENT_ACTIONS_MENU_VARIANTS = {
  CARD: 'card',
  HISTORY: 'history',
};

const iconButtonSx = {
  width: '28px',
  height: '24px',
  border: 'none',
  padding: 0,
  background: 'transparent',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const menuPaperSx = {
  borderRadius: '8px',
  boxShadow: '0px 4px 16px rgba(0, 0, 51, 0.12)',
};

const menuItemSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
  minWidth: '220px',
  fontFamily: PRIMARY_FONT[400],
  fontSize: '14px',
  color: BLACK[100],
  py: 1,
  px: 1.5,
};

const menuItemLabelSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  fontFamily: 'inherit',
  fontSize: 'inherit',
};

const menuIconSx = {
  color: PRIMARY_PURPLE[70],
  fontSize: '18px',
};

const STUB_ITEMS_BY_VARIANT = {
  [DOCUMENT_ACTIONS_MENU_VARIANTS.CARD]: [
    {
      key: 'userUploads',
      labelKey: 'userDetail.employeeDocumentation.menu.userUploads',
      hasSubmenu: true,
    },
    {
      key: 'sendToCandidate',
      labelKey: 'userDetail.employeeDocumentation.menu.sendToCandidate',
    },
    {
      key: 'downloadForm',
      labelKey: 'userDetail.employeeDocumentation.menu.downloadForm',
    },
  ],
  [DOCUMENT_ACTIONS_MENU_VARIANTS.HISTORY]: [
    {
      key: 'userUploads',
      labelKey: 'userDetail.employeeDocumentation.menu.userUploads',
      Icon: AttachFileOutlinedIcon,
      hasSubmenu: true,
    },
    {
      key: 'sendToCandidate',
      labelKey: 'userDetail.employeeDocumentation.menu.sendToCandidate',
      Icon: IosShareOutlinedIcon,
      hasSubmenu: false,
    },
  ],
};

const DocumentActionsMenu = ({
  document,
  isActionLoading,
  onRequestDelete,
  onSelectFormStatus,
  onSelectFormValidity,
  onSelectSetExpiration,
  onUploadHistory,
  onView,
  variant,
}) => {
  const sessionUser = useSelector(selectUser);
  const canWriteReviewState = canWriteDocumentReviewState(sessionUser?.role);
  const {
    anchorEl,
    handleMenuClose,
    handleMenuOpen,
    handleSubmenuClose,
    handleSubmenuOpen,
    isMenuOpen,
    isSubmenuOpen,
    submenuAnchorEl,
  } = useDocumentMenu();

  const showIcons = variant === DOCUMENT_ACTIONS_MENU_VARIANTS.HISTORY;
  const ariaLabelKey =
    variant === DOCUMENT_ACTIONS_MENU_VARIANTS.HISTORY
      ? 'userDetail.employeeDocumentation.history.menu.ariaLabel'
      : 'userDetail.employeeDocumentation.menu.ariaLabel';

  const handleMenuItemClick = (action) => () => {
    handleMenuClose();
    action();
  };

  const handleSelectFormStatus = (status) => {
    handleMenuClose();
    onSelectFormStatus(document._id, status);
  };

  const handleSelectFormValidity = (status) => {
    handleMenuClose();
    onSelectFormValidity(document._id, status);
  };

  const handleSelectSetExpiration = () => {
    handleMenuClose();
    onSelectSetExpiration(document);
  };

  const handleRequestDelete = () => {
    handleMenuClose();
    onRequestDelete(document);
  };

  const showDelete =
    variant === DOCUMENT_ACTIONS_MENU_VARIANTS.HISTORY &&
    document?.state?.status !== documentStateStatuses.voided;

  const leadingItems =
    variant === DOCUMENT_ACTIONS_MENU_VARIANTS.CARD
      ? [
          {
            key: 'viewDocument',
            labelKey: 'userDetail.employeeDocumentation.menu.viewDocument',
            onClick: onView,
            disabled: isActionLoading,
          },
          {
            key: 'uploadHistory',
            labelKey: 'userDetail.employeeDocumentation.menu.uploadHistory',
            onClick: onUploadHistory,
          },
        ]
      : [];

  const trailingItems = STUB_ITEMS_BY_VARIANT[variant];

  const renderMenuItem = (item) => {
    const isDisabled = item.disabled || !item.onClick;
    const { Icon } = item;
    const showsChevron = item.hasSubmenu === true;

    return (
      <MenuItem
        key={item.key}
        disabled={isDisabled}
        onClick={item.onClick ? handleMenuItemClick(item.onClick) : undefined}
        sx={menuItemSx}
      >
        <Typography component="span" sx={menuItemLabelSx}>
          {showIcons && Icon ? <Icon sx={menuIconSx} /> : null}
          {LanguageConverterV2(item.labelKey)}
        </Typography>
        {showsChevron && <ChevronRightIcon sx={{ color: BLACK[60], fontSize: '18px' }} />}
      </MenuItem>
    );
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleMenuOpen}
        sx={iconButtonSx}
        aria-label={LanguageConverterV2(ariaLabelKey)}
      >
        <DocumentCardMenuIcon />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        sx={{ zIndex: 1500 }}
        PaperProps={{ sx: menuPaperSx }}
      >
        {leadingItems.map(renderMenuItem)}
        {canWriteReviewState && (
          <>
            <DocumentReviewSubmenu
              Icon={EditOutlinedIcon}
              isSubmenuOpen={isSubmenuOpen(DOCUMENT_SUBMENU_KEYS.FORM_STATUS)}
              labelKey="userDetail.employeeDocumentation.menu.formStatus"
              onSubmenuOpen={handleSubmenuOpen(DOCUMENT_SUBMENU_KEYS.FORM_STATUS)}
              onSubmenuClose={handleSubmenuClose}
              showIcon={showIcons}
              submenuAnchorEl={submenuAnchorEl}
            >
              <FormStatusMenuItems document={document} onSelect={handleSelectFormStatus} />
            </DocumentReviewSubmenu>
            <DocumentReviewSubmenu
              Icon={DoneAllOutlinedIcon}
              isSubmenuOpen={isSubmenuOpen(DOCUMENT_SUBMENU_KEYS.FORM_VALIDITY)}
              labelKey="userDetail.employeeDocumentation.menu.formValidity"
              onSubmenuOpen={handleSubmenuOpen(DOCUMENT_SUBMENU_KEYS.FORM_VALIDITY)}
              onSubmenuClose={handleSubmenuClose}
              showIcon={showIcons}
              submenuAnchorEl={submenuAnchorEl}
            >
              <FormValidityMenuItems document={document} onSelect={handleSelectFormValidity} />
            </DocumentReviewSubmenu>
            <MenuItem onClick={handleSelectSetExpiration} sx={menuItemSx}>
              <Typography component="span" sx={menuItemLabelSx}>
                {showIcons ? <EventOutlinedIcon sx={menuIconSx} /> : null}
                {LanguageConverterV2('userDetail.employeeDocumentation.menu.setExpiration')}
              </Typography>
            </MenuItem>
            {showDelete && (
              <MenuItem onClick={handleRequestDelete} sx={menuItemSx}>
                <Typography component="span" sx={menuItemLabelSx}>
                  <DeleteOutlineIcon sx={menuIconSx} />
                  {LanguageConverterV2('userDetail.employeeDocumentation.menu.delete')}
                </Typography>
              </MenuItem>
            )}
          </>
        )}
        {trailingItems.map(renderMenuItem)}
      </Menu>
    </>
  );
};

DocumentActionsMenu.propTypes = {
  document: PropTypes.shape({
    _id: PropTypes.string,
    state: PropTypes.shape({ status: PropTypes.string }),
    validity: PropTypes.shape({
      status: PropTypes.string,
      range: PropTypes.shape({
        validFrom: PropTypes.string,
        validNoticeFrom: PropTypes.string,
        validThrough: PropTypes.string,
      }),
    }),
  }).isRequired,
  isActionLoading: PropTypes.bool,
  onRequestDelete: PropTypes.func,
  onSelectFormStatus: PropTypes.func,
  onSelectFormValidity: PropTypes.func,
  onSelectSetExpiration: PropTypes.func,
  onUploadHistory: PropTypes.func,
  onView: PropTypes.func,
  variant: PropTypes.oneOf(Object.values(DOCUMENT_ACTIONS_MENU_VARIANTS)).isRequired,
};

DocumentActionsMenu.defaultProps = {
  isActionLoading: false,
  onRequestDelete: () => {},
  onSelectFormStatus: () => {},
  onSelectFormValidity: () => {},
  onSelectSetExpiration: () => {},
  onUploadHistory: () => {},
  onView: () => {},
};

export default DocumentActionsMenu;
